import {
  ApiError,
  type BillingMachineResource,
  BillingMachinesController_list_v1,
  type MachineImportBill,
  type MachineImportCounts,
  type MachineImportDay,
  messageForErrorCode,
} from '@repo/api-client';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Badge } from '@repo/ui/components/badge';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconAlertTriangle } from '@tabler/icons-react';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { Link } from 'react-router';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { messageForFailure } from '@/api/failure';
import { formatPaise } from '@/lib/money';

import { ScreenTable } from '../back-office/Screen';
import type { MappingDraft } from '../billing-machines/MapProductCodeDialog';
import { billingMachineProductCodesPath, machineBillPath } from '../paths';
import { formatBusinessDate } from '../tickets/business-date';

/**
 * What an upload did, or would do: the counts, the days it lands on and every
 * bill's outcome. One component for the preview, the recorded answer and an
 * import read back later, because all three are the same `MachineImportResource`
 * and a person comparing the preview with the result should see one layout.
 *
 * **Every cell echoed from the file is rendered as text.** `detail.productName`
 * and the rest are the file's own bytes; React escapes them, and nothing here
 * puts one in an attribute a browser or a spreadsheet would act on.
 */

const OUTCOME_LABEL: Readonly<Record<MachineImportBill['outcome'], string>> = {
  CREATED: 'Created',
  ALREADY_IMPORTED: 'Already imported',
  // Produced by nothing until CL-1101 is answered. Rendered, not designed for.
  SKIPPED: 'Skipped',
  REFUSED: 'Refused',
};

const OUTCOME_VARIANT = {
  CREATED: 'secondary',
  ALREADY_IMPORTED: 'outline',
  SKIPPED: 'outline',
  REFUSED: 'destructive',
} as const satisfies Record<MachineImportBill['outcome'], string>;

/**
 * Map a refused bill's code without leaving the screen (#238). Given by the
 * upload screen, which holds the picked `File` and cannot navigate away from
 * it — a browser lets nothing re-pick a file for you. Without it the refusal
 * links to the machine's product codes, which is right for a recorded upload
 * read back later: that file has been submitted and nothing is lost.
 */
export type MapCode = (machineId: string, draft: MappingDraft) => void;

const inlineAction = 'text-foreground underline decoration-dotted underline-offset-4';

/**
 * The counts, named. Already imported is its own figure and never folded into
 * the refusals: a second upload of the same file is all "already imported", and
 * it must read as the success it is.
 */
export function Tally({ counts, preview }: { counts: MachineImportCounts; preview: boolean }) {
  return (
    <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <Figure term={preview ? 'Will be created' : 'Created'}>{counts.created}</Figure>
      <Figure term="Already imported">{counts.alreadyImported}</Figure>
      <Figure term={preview ? 'Will be refused' : 'Refused'}>{counts.refused}</Figure>
      {counts.skipped > 0 && <Figure term="Skipped">{counts.skipped}</Figure>}
    </dl>
  );
}

function Figure({ term, children }: { term: string; children: number }) {
  return (
    <div className="rounded-2xl border border-border bg-card px-4 py-3">
      <dt className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
        {term}
      </dt>
      <dd className="mt-1 font-mono text-2xl text-foreground numeric">{children}</dd>
    </div>
  );
}

/** The trading days the created bills land on — each its outlet's own day. */
export function Days({ days, preview }: { days: readonly MachineImportDay[]; preview: boolean }) {
  return (
    <Band name={preview ? 'Days it will add to' : 'Days it added to'}>
      {days.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {preview ? 'No bill in this file would be created.' : 'No bill in this file was created.'}
        </p>
      ) : (
        <ScreenTable label="Trading days" columns="stated">
          <TableHeader>
            <TableRow>
              <TableHead className="w-36">Trading day</TableHead>
              <TableHead className="w-56">Outlet</TableHead>
              <TableHead className="w-24 text-right">Bills</TableHead>
              <TableHead className="w-32 text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {days.map((day) => (
              <TableRow key={`${day.outletId}:${day.businessDate}`}>
                <TableCell className="numeric">{formatBusinessDate(day.businessDate)}</TableCell>
                <TableCell>{day.outletName}</TableCell>
                <TableCell className="text-right font-mono numeric">{day.createdBills}</TableCell>
                <TableCell className="text-right font-mono numeric">
                  {formatPaise(day.createdTotalPaise)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </ScreenTable>
      )}
    </Band>
  );
}

/**
 * Every bill, refused ones first in their own band. A refused bill never held
 * the rest back, so the band says so rather than reading as a failed file.
 */
export function Bills({
  bills,
  preview,
  onMap,
}: {
  bills: readonly MachineImportBill[];
  preview: boolean;
  onMap?: MapCode;
}) {
  const refused = bills.filter((bill) => bill.outcome === 'REFUSED');
  const rest = bills.filter((bill) => bill.outcome !== 'REFUSED');
  const machineIds = useMachineIds(refused.some((bill) => needsMapping(bill.code)));

  return (
    <>
      {refused.length > 0 && (
        <Band
          name={`${String(refused.length)} ${refused.length === 1 ? 'bill' : 'bills'} ${preview ? 'would be' : refused.length === 1 ? 'was' : 'were'} refused`}
        >
          <p className="text-sm text-muted-foreground">
            {preview
              ? 'These would not be recorded. The rest of the file is not held back by them.'
              : 'These were not recorded; the rest of the file was.'}{' '}
            Put right what each one names and upload the same file again — bills already imported
            are not counted twice.
          </p>
          <BillTable bills={refused} machineIds={machineIds} onMap={onMap} label="Refused bills" />
        </Band>
      )}

      <Band
        name={`${refused.length > 0 ? 'The other ' : ''}${String(rest.length)} ${rest.length === 1 ? 'bill' : 'bills'}`}
      >
        {rest.length === 0 ? (
          <p className="text-sm text-muted-foreground">No bill in this file is recorded.</p>
        ) : (
          <BillTable bills={rest} machineIds={machineIds} onMap={onMap} label="Bills" />
        )}
      </Band>
    </>
  );
}

function BillTable({
  bills,
  machineIds,
  onMap,
  label,
}: {
  bills: readonly MachineImportBill[];
  machineIds: ReadonlyMap<string, string>;
  onMap?: MapCode;
  label: string;
}) {
  return (
    <ScreenTable label={label}>
      <TableHeader>
        <TableRow>
          {/* Header is row 1, so these are what a spreadsheet shows. */}
          <TableHead className="w-24">Rows</TableHead>
          <TableHead className="w-28">Receipt</TableHead>
          <TableHead className="w-36">Machine</TableHead>
          <TableHead className="w-32">Bill date</TableHead>
          <TableHead>Outcome</TableHead>
          <TableHead className="w-28 text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {bills.map((bill) => (
          <TableRow key={`${bill.machineCode}:${bill.receiptNumber}:${String(bill.rows[0])}`}>
            <TableCell className="align-top font-mono text-muted-foreground numeric">
              {rowsLabel(bill.rows)}
            </TableCell>
            <TableCell className="align-top font-mono numeric">
              {bill.saleId != null && bill.outcome === 'CREATED' ? (
                <Link
                  to={machineBillPath(bill.saleId)}
                  className="underline decoration-border decoration-dotted underline-offset-4 hover:decoration-current"
                >
                  {bill.receiptNumber}
                </Link>
              ) : (
                bill.receiptNumber
              )}
            </TableCell>
            <TableCell className="align-top font-mono numeric">{bill.machineCode}</TableCell>
            <TableCell className="align-top numeric">{formatBusinessDate(bill.billDate)}</TableCell>
            <TableCell className="align-top whitespace-normal">
              <Badge variant={OUTCOME_VARIANT[bill.outcome]}>{OUTCOME_LABEL[bill.outcome]}</Badge>
              <Explanation bill={bill} machineId={machineIds.get(bill.machineCode)} onMap={onMap} />
            </TableCell>
            <TableCell className="text-right align-top font-mono numeric">
              {bill.totalPaise == null ? '' : formatPaise(bill.totalPaise)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </ScreenTable>
  );
}

/** Contiguous rows as a range — `4–6` — and anything else as a list. */
function rowsLabel(rows: readonly number[]): string {
  const [first] = rows;
  const last = rows.at(-1);
  if (first === undefined || last === undefined) return '';
  if (rows.length > 1 && last - first === rows.length - 1)
    return `${String(first)}–${String(last)}`;
  return rows.join(', ');
}

const needsMapping = (code: string | null | undefined): boolean =>
  code === 'MACHINE_PRODUCT_CODE_UNMAPPED' || code === 'MACHINE_PRODUCT_CODE_REPROGRAMMED';

/** A detail value as text, or `null`. Only strings and numbers are shown. */
function cell(detail: MachineImportBill['detail'], key: string): string | null {
  const value = detail?.[key];
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return null;
}

function Explanation({
  bill,
  machineId,
  onMap,
}: {
  bill: MachineImportBill;
  machineId?: string;
  onMap?: MapCode;
}) {
  const note = 'mt-1.5 text-sm text-muted-foreground';

  if (bill.outcome !== 'REFUSED') {
    const differs = bill.rateDiffersFromCatalogue ?? [];
    return differs.length === 0 ? null : (
      <p className={note}>
        The machine&rsquo;s rate differs from the catalogue price for{' '}
        {differs.length === 1 ? 'code' : 'codes'}{' '}
        <span className="font-mono numeric">{differs.join(', ')}</span>. The machine&rsquo;s amount
        is what is recorded.
      </p>
    );
  }

  const code = cell(bill.detail, 'productCode');
  const printed = cell(bill.detail, 'productName');
  const mapped = cell(bill.detail, 'mappedName');
  const column = cell(bill.detail, 'column');
  const value = cell(bill.detail, 'value');
  const row = cell(bill.detail, 'row');
  const saleId = cell(bill.detail, 'saleId');

  return (
    <div className={note}>
      <p>{messageForErrorCode(bill.code ?? undefined)}</p>

      {needsMapping(bill.code) && code !== null && (
        <p className="mt-1">
          Code <span className="font-mono text-foreground numeric">{code}</span>
          {printed !== null && (
            <>
              , printed as <span className="text-foreground">{printed}</span>
            </>
          )}
          {mapped !== null && (
            <>
              , mapped as <span className="text-foreground">{mapped}</span>
            </>
          )}
          .{' '}
          {machineId !== undefined &&
            (onMap === undefined ? (
              <Link
                to={billingMachineProductCodesPath(machineId, {
                  code,
                  ...(printed === null ? {} : { name: printed }),
                })}
                className={inlineAction}
              >
                Map this code
              </Link>
            ) : (
              <>
                <button
                  type="button"
                  className={inlineAction}
                  onClick={() => {
                    onMap(machineId, {
                      code,
                      name: printed ?? '',
                      // A reprogrammed code is already mapped; the form fixes
                      // the printed name and leaves the code itself alone.
                      remapping: bill.code === 'MACHINE_PRODUCT_CODE_REPROGRAMMED',
                    });
                  }}
                >
                  Map this code
                </button>
                {' · '}
                <Link
                  to={billingMachineProductCodesPath(machineId)}
                  className="underline decoration-dotted underline-offset-4 hover:text-foreground"
                >
                  All this machine&rsquo;s codes
                </Link>
              </>
            ))}
        </p>
      )}

      {bill.code === 'MACHINE_BILL_AMOUNT_INVALID' && column !== null && (
        <p className="mt-1">
          Column <span className="text-foreground">{column}</span>
          {value !== null && (
            <>
              : <span className="font-mono text-foreground">{value}</span>
            </>
          )}
        </p>
      )}

      {bill.code === 'MACHINE_BILL_DISCOUNT_EXCEEDS_AMOUNT' && row !== null && (
        <p className="mt-1">
          Row <span className="font-mono text-foreground numeric">{row}</span>
          {value !== null && (
            <>
              : discount <span className="font-mono text-foreground">{value}</span>
            </>
          )}
        </p>
      )}

      {bill.code === 'MACHINE_BILL_DIFFERS' && saleId !== null && (
        <p className="mt-1">
          <Link to={machineBillPath(saleId)} className={inlineAction}>
            The bill already imported
          </Link>
        </p>
      )}
    </div>
  );
}

/**
 * Machine code to machine id, for the "map this code" link. Read whole — the
 * park has a handful of machines — and only when a refusal needs it. A failed
 * read costs the link, never the outcomes.
 */
function useMachineIds(needed: boolean): ReadonlyMap<string, string> {
  const client = useApiClient();
  const [ids, setIds] = useState<ReadonlyMap<string, string>>(new Map());

  useEffect(() => {
    if (!needed) return;

    const controller = new AbortController();
    let live = true;

    collectPages<BillingMachineResource>((offset, limit) =>
      client.request(BillingMachinesController_list_v1, {
        query: { offset, limit },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setIds(new Map(all.map((machine) => [machine.machineCode, machine.id])));
      })
      .catch(() => undefined);

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, needed]);

  return ids;
}

/**
 * A whole-file refusal: nothing was written. The two that name something in
 * the file — the columns, the unreadable rows — show it.
 */
export function FileRefused({ error }: { error: unknown }) {
  const details = error instanceof ApiError ? error.details : undefined;
  const code = error instanceof ApiError ? error.code : undefined;

  const found = listOf(details?.found);
  const expected = listOf(details?.expected);
  const rows = Array.isArray(details?.rows) ? (details.rows as unknown[]) : [];

  return (
    <div className="space-y-6">
      <Alert variant="destructive">
        <IconAlertTriangle />
        <AlertTitle>Nothing in this file was imported</AlertTitle>
        <AlertDescription>{messageForFailure(error)}</AlertDescription>
      </Alert>

      {code === 'MACHINE_IMPORT_HEADER_UNRECOGNISED' && (found !== null || expected !== null) && (
        <dl className="grid gap-4 text-sm md:grid-cols-2">
          {expected !== null && (
            <div>
              <dt className="text-muted-foreground">A machine export has</dt>
              <dd className="mt-1 font-mono break-words text-foreground">{expected}</dd>
            </div>
          )}
          {found !== null && (
            <div>
              <dt className="text-muted-foreground">This file has</dt>
              <dd className="mt-1 font-mono break-words text-foreground">{found}</dd>
            </div>
          )}
        </dl>
      )}

      {code === 'MACHINE_IMPORT_ROWS_UNREADABLE' && rows.length > 0 && (
        <ScreenTable label="Unreadable rows">
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Row</TableHead>
              <TableHead>Why</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((entry, index) => {
              const row = entry as { row?: unknown; reason?: unknown };
              return (
                <TableRow key={index}>
                  <TableCell className="font-mono text-muted-foreground numeric">
                    {typeof row.row === 'number' ? row.row : ''}
                  </TableCell>
                  <TableCell className="whitespace-normal text-muted-foreground">
                    {typeof row.reason === 'string' ? row.reason : ''}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </ScreenTable>
      )}
    </div>
  );
}

function listOf(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(String).join(', ');
  return null;
}

/** A named band of the page — the same composition as `CatalogueUpload`'s. */
export function Band({ name, children }: { name: string; children: ReactNode }) {
  const headingId = useId();

  return (
    <section aria-labelledby={headingId}>
      <header className="border-b border-border pb-3">
        <h2 id={headingId} className="font-medium text-foreground">
          {name}
        </h2>
      </header>
      <div className="space-y-4 pt-4">{children}</div>
    </section>
  );
}
